import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FileHeader, HeaderMapping, InvalidValues, PortfolioPropertiesMap, PropertyConfig } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { NonCancelableCustomEvent, SelectProps, Spinner } from '@amzn/awsui-components-react';
import React, { Component } from 'react';

import { constants } from '../../../common/Localization';
import { InvalidDataModal } from './common-components/InvalidDataModal';
import { WIZARD_STEP_VALUES } from './common-util/resources';
import { OnDuplicatesChange } from './common-util/types';
import { convertStringToNumber, isEmpty } from './common-util/util';
import { withWizardContext } from './contexts/WizardContext';
import MandatoryPropertiesTable from './header-mapping/MandatoryPropertiesTable';
import OptionalPropertiesTable from './header-mapping/OptionalPropertiesTable';
import { clearOverwriteCounts, setOverwriteCountForOneColumn, setOverwriteCounts } from './header-mapping/OverwriteHelpers';
import { OverwriteModal } from './header-mapping/OverwriteModal';
import { WizardContentProps } from './WizardContentProps';

interface Props extends WizardContentProps, FlashContextInterface {
    onDuplicatesChange: OnDuplicatesChange;
}

interface State {
    dataRows: string[][];
    fileHeaders: FileHeader[];
    invalidModalVisible: boolean;
    invalidValues: InvalidValues;
    mandatoryProperties: PropertyConfig[];
    portfolioProperties: PortfolioPropertiesMap;
    optionalProperties: PropertyConfig[];
    overwriteModalVisible: boolean;
}

const stepName: WIZARD_STEP_VALUES = WIZARD_STEP_VALUES.HEADER_MAPPING;
const importService = new ImportService();

class HeaderMappingStep extends Component<Props, State> {

    currentPortfolioPropertyItem: HeaderMapping;

    constructor(props: Props) {
        super(props);
        this.currentPortfolioPropertyItem = { fileHeader: '', portfolioProperty: '' };
        this.state = {
            dataRows: [],
            fileHeaders: [],
            invalidModalVisible: false,
            invalidValues: {},
            mandatoryProperties: [],
            optionalProperties: [],
            overwriteModalVisible: false,
            portfolioProperties: {},
        };
    }

    async componentDidMount() {
        this.props.setNextAndPrevHandlers(stepName, this.next, this.prev);
        const { propertiesConfig, setLoading, wizardContext } = this.props;
        const { portfolioId, importId, type } = wizardContext;
        const mandatoryProperties = propertiesConfig.filter(property => !property.optional);
        const optionalProperties = propertiesConfig.filter(property => property.optional);

        optionalProperties.unshift({value: 'ignore', text: constants.DO_NOT_IMPORT, id: 'ignore', label: constants.DO_NOT_IMPORT});
        this.setState({
            mandatoryProperties,
            optionalProperties,
        });

        try {
            const { fileHeaders, portfolioProperties, dataRows, selectedSheetIndex } = await importService.getHeaderMappings(portfolioId, importId);
            if (!fileHeaders || !portfolioProperties) {
                return;
            }
            const fileHeadersToDisplay = fileHeaders.map((fileHeader: FileHeader, index: number) => {
                if (fileHeader.match && fileHeader.match !== 'ignore') {
                    portfolioProperties[fileHeader.match].fileHeaderIndex = index;
                }
                return {
                    id: index,
                    ...fileHeader,
                };
            });

            this.state.optionalProperties.forEach((optionalProperty: PropertyConfig) => {
                if (portfolioProperties[optionalProperty.value]) {
                    portfolioProperties[optionalProperty.value].optional = true;
                }
            });
            setOverwriteCounts(portfolioProperties, this.props.existingDataMap, dataRows, type);
            this.setState({
                dataRows,
                fileHeaders: fileHeadersToDisplay,
                invalidModalVisible: !isEmpty(this.props.sheets[selectedSheetIndex].invalidValues),
                invalidValues: this.props.sheets[selectedSheetIndex].invalidValues || {},
                portfolioProperties,
            });
        } catch (err) {
            this.props.flash.alert(FlashType.error, (err as Error).message, constants.ERR_HEADERS.CANNOT_LOAD_HEADER_MAPPINGS);
        } finally {
            setLoading(false);
        }
    }

    render() {
        if (this.props.loading) {
            return <Spinner size='large' />;
        }
        const { overwriteModalVisible, mandatoryProperties, optionalProperties, portfolioProperties,
            fileHeaders, invalidModalVisible, invalidValues } = this.state;
        const idFields: HeaderMapping[] = Object.values(portfolioProperties).filter((property: HeaderMapping) => property.isId);
        const overwriteModal = OverwriteModal(overwriteModalVisible, this.currentPortfolioPropertyItem,
            idFields, this.props.propertiesConfig, this.onDismissOverwriteModal);
        return (
            <div>
                <MandatoryPropertiesTable
                    {...this.props}
                    mandatoryProperties={mandatoryProperties}
                    fileHeaders={fileHeaders}
                    portfolioProperties={portfolioProperties}
                    onMandatoryHeaderMappingChange={this.onMandatoryHeaderMappingChange}
                    onUomChange={this.onUomChange}
                    onOpenOverwriteModal={this.onOpenOverwriteModal}
                    onSwitchOverwriteToggle={this.onSwitchOverwriteToggle}
                />
                <OptionalPropertiesTable
                    {...this.props}
                    optionalProperties={optionalProperties}
                    fileHeaders={fileHeaders}
                    portfolioProperties={portfolioProperties}
                    onOptionalHeaderMappingChange={this.onOptionalHeaderMappingChange}
                    onUomChange={this.onUomChange}
                    onOpenOverwriteModal={this.onOpenOverwriteModal}
                    onSwitchOverwriteToggle={this.onSwitchOverwriteToggle}
                />
                {overwriteModal}
                <InvalidDataModal
                    visible={invalidModalVisible}
                    invalidValues={invalidValues}
                    cancel={this.props.cancel}
                    continue={this.continue}
                />
            </div>
        );
    }

    next = async (): Promise<void> => {
        this.validateMappings();
        const { portfolioId, importId, type } = this.props.wizardContext;
        await importService.saveHeaderMappings(portfolioId, importId, this.state.portfolioProperties, this.state.fileHeaders);
        const result = await importService.getDuplicateRecords(portfolioId, importId, type);
        const { duplicateRecordsMap } = result;
        this.props.onDuplicatesChange(duplicateRecordsMap);
    };

    prev = async (): Promise<void> => {};

    /**
     * Throw error if header-mapping requirements are not met
     */
    validateMappings = (): void => {
        const { portfolioProperties } = this.state;
        const { propertiesConfig } = this.props;
        const unmappedIdFields: PropertyConfig[] = [];
        const missingUOMFields: PropertyConfig[] = [];

        propertiesConfig.forEach((propertyConfig: PropertyConfig) => {
            if (propertyConfig.isId && !portfolioProperties[propertyConfig.value]?.fileHeaderIndex) {
                unmappedIdFields.push(propertyConfig);
            }
            if (propertyConfig.UOM && portfolioProperties[propertyConfig.value]?.fileHeaderIndex
                && !portfolioProperties[propertyConfig.value]?.fileUOM) {
                missingUOMFields.push(propertyConfig);
            }
        });

        if (unmappedIdFields.length) {
            const errMsgWithUnmappedIDs = `${constants.ERR_UNMAPPED_ID_FIELD}${unmappedIdFields.map(idField => idField.label).join(', ')}`;
            throw new Error(errMsgWithUnmappedIDs);
        }
        if (missingUOMFields.length) {
            const errMsgWithMissingUOMs = `${constants.ERR_MISSING_UOM}${missingUOMFields.map(field => field.label).join(', ')}`;
            throw new Error(errMsgWithMissingUOMs);
        }
    };

    onOpenOverwriteModal = (item: HeaderMapping): void => {
        this.currentPortfolioPropertyItem = item;
        this.setState({
            overwriteModalVisible: true,
        });
    };

    onDismissOverwriteModal = (): void => {
        this.setState({
            overwriteModalVisible: false,
        });
    };

    onMandatoryHeaderMappingChange = async (item: HeaderMapping, e: NonCancelableCustomEvent<SelectProps.ChangeDetail>): Promise<void> => {
        const { portfolioProperties, fileHeaders, dataRows } = this.state;
        const portfolioPropertiesCopy = {...portfolioProperties} as PortfolioPropertiesMap;
        const fileHeadersCopy = [...fileHeaders] as FileHeader[];
        const newFileHeader = e.detail.selectedOption.label;
        const newFileHeaderIndex = convertStringToNumber(e.detail.selectedOption.value || '');
        if (!item.portfolioProperty || !portfolioPropertiesCopy[item.portfolioProperty] || !fileHeadersCopy[newFileHeaderIndex]) {
            return;
        }
        const oldFileHeaderIndex = portfolioPropertiesCopy[item.portfolioProperty].fileHeaderIndex;
        if (newFileHeaderIndex === oldFileHeaderIndex) {
            return;
        }

        // Remove other occurrence of the new file header
        const oldProperty = fileHeadersCopy[newFileHeaderIndex].match;
        if (oldProperty && portfolioPropertiesCopy[oldProperty]) {
            portfolioPropertiesCopy[oldProperty] = {
                ...portfolioPropertiesCopy[oldProperty],
                fileHeader: '',
                fileHeaderIndex: 0,
                overwriteCount: 0,
            };
        }

        // Map new file header to portfolio property
        // skip 'Do not import' at index 0
        if (newFileHeaderIndex >= 1) {
            fileHeadersCopy[newFileHeaderIndex].match = item.portfolioProperty;
        }

        // Map old file header to null
        // skip 'Do not import' at index 0
        if (oldFileHeaderIndex && oldFileHeaderIndex >= 1) {
            fileHeadersCopy[oldFileHeaderIndex].match = '';
        }

        // Map portfolio header to the new file header
        portfolioPropertiesCopy[item.portfolioProperty].fileHeaderIndex = newFileHeaderIndex;
        portfolioPropertiesCopy[item.portfolioProperty].fileHeader = newFileHeader || '';
        const { type } = this.props.wizardContext;

        if (item.isId || oldProperty && portfolioPropertiesCopy[oldProperty] && portfolioPropertiesCopy[oldProperty].isId) {
            // In case when ID is mapped to another file header, recalculate all overwrite counts
            if (newFileHeaderIndex) {
                setOverwriteCounts(portfolioPropertiesCopy, this.props.existingDataMap, dataRows, type);
            } else {
                // In case when Id is mapped to null, overwrite count column should be set to all 0
                clearOverwriteCounts(portfolioPropertiesCopy);
            }
        } else {
            setOverwriteCountForOneColumn(
                portfolioPropertiesCopy[item.portfolioProperty],
                portfolioPropertiesCopy,
                this.props.existingDataMap,
                dataRows,
                type,
            );
        }

        this.setState({
            fileHeaders: fileHeadersCopy,
            portfolioProperties: portfolioPropertiesCopy,
        });
    };

    onOptionalHeaderMappingChange = (item: HeaderMapping, e: any): void => {
        const { portfolioProperties, fileHeaders, dataRows } = this.state;
        const portfolioPropertiesCopy = {...portfolioProperties} as PortfolioPropertiesMap;
        const fileHeadersCopy = [...fileHeaders] as FileHeader[];

        const newProperty = e.detail.selectedOption.id;
        const fileHeaderIndex = item.fileHeaderIndex;
        if (fileHeaderIndex == null || !fileHeadersCopy[fileHeaderIndex] || !newProperty) {
            return;
        }
        const oldProperty = fileHeadersCopy[fileHeaderIndex]?.match || 'ignore';
        if (oldProperty === newProperty) {
            return;
        }

        // Remove other occurrence of new portfolio property
        const oldFileHeaderIndex = portfolioPropertiesCopy[newProperty]?.fileHeaderIndex;
        if (oldFileHeaderIndex) {
            fileHeadersCopy[oldFileHeaderIndex].match = '';
        }

        // Map file header to new portfolio property
        fileHeadersCopy[fileHeaderIndex].match = newProperty;

        // Map old portfolio property to null
        if (portfolioPropertiesCopy[oldProperty]) {
            portfolioPropertiesCopy[oldProperty] = {
                ...portfolioPropertiesCopy[oldProperty],
                fileHeader: '',
                fileHeaderIndex: 0,
                overwriteCount: 0,
            };
        }

        // Map new portfolio property to file header
        if (portfolioPropertiesCopy[newProperty]) {
            portfolioPropertiesCopy[newProperty].fileHeader = item.fileHeader;
            portfolioPropertiesCopy[newProperty].fileHeaderIndex = item.fileHeaderIndex;
            // Recount overwrite records for current portfolio property
            setOverwriteCountForOneColumn(
                portfolioPropertiesCopy[newProperty],
                portfolioPropertiesCopy,
                this.props.existingDataMap,
                dataRows,
                this.props.wizardContext.type,
            );
        }

        this.setState({
            fileHeaders: fileHeadersCopy,
            portfolioProperties: portfolioPropertiesCopy,
        });
    };

    onUomChange = (item: HeaderMapping, e: any): void => {
        const { dataRows, portfolioProperties } = this.state;
        const portfolioPropertiesCopy = {...portfolioProperties} as PortfolioPropertiesMap;
        const selectedUOM = e.detail.value;
        const portfolioProperty = item.portfolioProperty;
        if (!selectedUOM || !portfolioProperty || !portfolioPropertiesCopy[portfolioProperty]) {
            return;
        }
        portfolioPropertiesCopy[portfolioProperty].fileUOM = selectedUOM;
        // Recount overwrite records for current portfolio property
        setOverwriteCountForOneColumn(
            portfolioPropertiesCopy[portfolioProperty],
            portfolioPropertiesCopy,
            this.props.existingDataMap,
            dataRows,
            this.props.wizardContext.type,
        );
        this.setState({
            portfolioProperties: portfolioPropertiesCopy,
        });
    };

    onSwitchOverwriteToggle = (item: HeaderMapping, e: any): void => {
        const { portfolioProperties } = this.state;
        const portfolioPropertiesCopy = {...portfolioProperties} as PortfolioPropertiesMap;
        const checked = e.detail.checked;
        const portfolioProperty = item.portfolioProperty;
        if (!portfolioProperty || !portfolioPropertiesCopy[portfolioProperty]) {
            return;
        }
        portfolioPropertiesCopy[portfolioProperty].overwrite = checked;
        this.setState({
            portfolioProperties: portfolioPropertiesCopy,
        });
    };

    continue = (): void => {
        this.setState({
            invalidModalVisible: false,
        });
    };
}

export default withFlashContext(withWizardContext(HeaderMappingStep));
